import {CommonModule} from '@angular/common';
import {Component, AfterViewInit, ElementRef, ViewChild, Input} from '@angular/core';
import { NgbActiveModal, NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { CommonService } from '../../app/core/common/common.service';
import { AuGoldComponent } from './modals/au-gold/au-gold.component';
import { GuardianEliteComponent } from './modals/guardian-elite/guardian-elite.component';
import { GUARDIAN_ELITE, SPECIALTY_PRODUCTS } from '../submission-start-choice/configs/program-config.util';
import { ProductOption } from '../submission-start-choice/types/program-types';

@Component({
  selector: 'app-submission-start-choice',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './submission-start-choice.component.html',
  styleUrl: './submission-start-choice.component.scss'
})
export class SubmissionStartChoiceComponent implements AfterViewInit {

  @Input() public redirectOnCancel = false;
  @Input() public cancelRedirectRoute = 'dashboard';


  /** Static product configuration - see program-config.util.ts */
  public readonly guardianElite: ProductOption = GUARDIAN_ELITE;
  public readonly specialtyProducts: ProductOption[] = SPECIALTY_PRODUCTS;

  /** Guardian Elite is the recommended product, so it is selected by default. */
  public selected: string | null = this.guardianElite.id;

  /** Scrollable container */
  @ViewChild('cardList')
  cardList!: ElementRef<HTMLDivElement>;

  constructor(
    public readonly activeModal: NgbActiveModal,
    private readonly commonService: CommonService,
    private readonly modalService: NgbModal
  ) { }

  ngAfterViewInit(): void {
    // Scroll to the selected option when the modal is opened.
    setTimeout(() => this.scrollToSelected(), 0);
      setTimeout(() => (document.activeElement as HTMLElement)?.blur(), 0);
  }

  public get selectedProduct(): ProductOption | null {
    if (this.selected === this.guardianElite.id) {
      return this.guardianElite;
    }
    return this.specialtyProducts.find(product => product.id === this.selected) ?? null;
  }

  public select(id: string): void {
    this.selected = id;
    // Keep the selected item visible.
    setTimeout(() => this.scrollToSelected(), 0);
  }

  public cancel(): void {
    this.activeModal.dismiss();
    if (this.redirectOnCancel) {
      this.commonService.navigateToURLWithtenantCode(this.cancelRedirectRoute);
    }
  }

  public continue(): void {
    const product = this.selectedProduct;

    if (!product) {
      return;
    }
    this.commonService.setSkipDirtyGuard(true);
    this.activeModal.close();
    if (product.group === 'green-shield') {
      this.openGuardianElite(product);
    } else {
      this.openAuGold(product);
    }
  }

  /**
   * Scrolls the selected specialty product into view.
   */
  private scrollToSelected(): void {
    if (!this.cardList || !this.selected) {
      return;
    }

    // Guardian Elite is outside the scrollable list.
    if (this.selected === this.guardianElite.id) {
      this.cardList.nativeElement.scrollTop = 0;
      return;
    }

    const selectedCard = this.cardList.nativeElement.querySelector(
      `[data-id="${this.selected}"]`
    ) as HTMLElement | null;

    if (selectedCard) {
      selectedCard.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'nearest',
        
      });
    }
  }

  private openGuardianElite(product: ProductOption): void {
    const guardianModal: NgbModalRef = this.modalService.open(GuardianEliteComponent, {
      centered: true,
      backdrop: 'static',
      size: 'lg',
      keyboard: false,
    });

    guardianModal.componentInstance.selectedProduct = product;

    guardianModal.result
      .then((result: string) => {
        // result is 'scratch' | 'upload' - bubble the real value up.
        this.activeModal.close(result);
      })
      .catch(() => {
        // Dismissed (e.g. "Back") - this modal was never closed, it is
        // already visible again underneath; nothing further to do.
      });
  }

  private openAuGold(product: ProductOption): void {
    const auModal: NgbModalRef = this.modalService.open(AuGoldComponent, {
      centered: true,
      backdrop: 'static',
      size: 'md',
      keyboard: false,
    });

    auModal.componentInstance.selectedProduct = product;

    auModal.result.catch(() => {
      // Dismissed (e.g. "Back"/"Cancel") - this modal stays open underneath.
      this.activeModal.close();
    });
  }
}
